import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import heroImage from '../hero.png';
import { getImageUrl } from '../utils/imageHelper';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Fallback static slide while data loads
const FALLBACK_SLIDES = [
  {
    id: 'fallback',
    type: 'static',
    title: 'Discover Your Next Board Game Adventure',
    subtitle: 'Find local board game clubs, connect with players, and explore the world of tabletop gaming',
    cta: 'Get Started',
    ctaLink: '/register',
    image: null,
  }
];

function HeroCarousel() {
  const navigate = useNavigate();
  const [slides, setSlides] = useState(FALLBACK_SLIDES);
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [evRes, clubRes] = await Promise.allSettled([
          fetch(`${API_URL}/events?upcoming=true&limit=5`),
          fetch(`${API_URL}/clubs/featured`),
        ]);

        const built = [];

        if (evRes.status === 'fulfilled' && evRes.value.ok) {
          const evData = await evRes.value.json();
          if (evData.success && evData.data?.length) {
            evData.data.slice(0, 3).forEach(ev => {
              const date = new Date(ev.startDate).toLocaleDateString('en-US', {
                weekday: 'short', month: 'short', day: 'numeric'
              });
              built.push({
                id: `ev-${ev.id}`,
                type: 'event',
                title: ev.title,
                subtitle: `${ev.club?.name || ''} · ${date}${ev.startTime ? ' · ' + ev.startTime : ''}${ev.location ? ' · ' + ev.location : ''}`,
                cta: "RSVP — I'm Going!",
                ctaLink: `/events?id=${ev.id}`,
                image: ev.imageUrl || null,
                badge: 'Event',
              });
            });
          }
        }

        if (clubRes.status === 'fulfilled' && clubRes.value.ok) {
          const clubData = await clubRes.value.json();
          if (clubData.success && clubData.data?.length) {
            clubData.data.slice(0, 2).forEach(club => {
              built.push({
                id: `cl-${club.id}`,
                type: 'club',
                title: club.name,
                subtitle: `${club.location || ''} · ${club.memberCount || 0} members · ${club.games || 'Various games'}`,
                cta: 'View Club',
                ctaLink: `/clubs/${club.slug || club.id}`,
                image: getImageUrl(club.image),
                badge: 'Club',
              });
            });
          }
        }

        if (!cancelled && built.length > 0) {
          setSlides(built);
        }
      } catch (_) {}
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // Auto-advance
  useEffect(() => {
    if (paused || slides.length <= 1) return;
    const t = setInterval(() => setCurrent(c => (c + 1) % slides.length), 4500);
    return () => clearInterval(t);
  }, [slides.length, paused]);

  const goTo = (idx) => setCurrent(idx);
  const prev = () => setCurrent(c => (c - 1 + slides.length) % slides.length);
  const next = () => setCurrent(c => (c + 1) % slides.length);

  const slide = slides[current];

  return (
    <section
      className="relative text-white overflow-hidden"
      style={{ minHeight: '520px' }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={slide.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.7 }}
          className="absolute inset-0"
        >
          {/* Background image */}
          {slide.image ? (
            <img src={slide.image} alt={slide.title} className="w-full h-full object-cover absolute inset-0" />
          ) : (
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: `url(${heroImage})` }}
            />
          )}
          {/* Gradient overlays */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/85 via-purple-900/75 to-blue-900/85" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/60" />
        </motion.div>
      </AnimatePresence>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 py-24 md:py-36 max-w-5xl">
        <AnimatePresence mode="wait">
          <motion.div
            key={slide.id + '-content'}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            {slide.badge && (
              <span className="inline-block bg-white/20 backdrop-blur-sm text-white text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-4 border border-white/30">
                {slide.badge}
              </span>
            )}
            <h1 className="text-4xl md:text-6xl font-extrabold mb-4 leading-tight drop-shadow-lg">
              {slide.title}
            </h1>
            <p className="text-lg md:text-xl mb-8 text-blue-100 max-w-2xl mx-auto line-clamp-2">
              {slide.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate(slide.ctaLink)}
                className="btn btn-primary btn-lg shadow-xl hover:shadow-primary/40 transition-shadow"
              >
                {slide.cta}
              </button>
              <button
                onClick={() => navigate('/find-clubs')}
                className="btn btn-outline text-white border-white hover:bg-white hover:text-gray-900 btn-lg"
              >
                Browse Clubs
              </button>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Prev/Next arrows */}
      {slides.length > 1 && (
        <>
          <button
            onClick={prev}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full p-2 transition-colors"
          >
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={next}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full p-2 transition-colors"
          >
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}

      {/* Dot indicators */}
      {slides.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className={`rounded-full transition-all duration-300 ${
                i === current ? 'bg-white w-6 h-2.5' : 'bg-white/50 w-2.5 h-2.5'
              }`}
            />
          ))}
        </div>
      )}
    </section>
  );
}

export default HeroCarousel;
